"use client";

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, RefreshCcw } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import { auditApiV2Get } from "@/lib/auditApiV2";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";

const TZ = "Asia/Kuala_Lumpur";
const TZ_OFFSET = "+08:00";

type SummaryTimeseriesRow = {
  t: string; // ISO
  total: number;
  errors: number;
  error_rate_pct: number | null;
  p95_latency_ms: number | null;
};

type SummaryPayload = {
  range: { from: string; to: string; bucket: "day" | "hour"; tz: string };
  events: {
    total: number;
    errors: number;
    error_rate_pct: number | null;
    status: { "2xx": number; "4xx": number; "5xx": number };
    latency_ms: { p50: number | null; p90: number | null; p95: number | null; p99: number | null };
    top_services: any[];
    top_paths: any[];
    top_errors: any[];
    timeseries: SummaryTimeseriesRow[];
  };
  db_changes: { total: number; top_tables: any[]; timeseries: { t: string; total: number }[] };
};

type SummaryResponse = {
  current: SummaryPayload;
  previous: SummaryPayload | null;
  delta: {
    events_total: { current: number | null; previous: number | null; delta: number | null; delta_pct: number | null };
    events_errors: { current: number | null; previous: number | null; delta: number | null; delta_pct: number | null };
    error_rate_pct: { current: number | null; previous: number | null; delta: number | null; delta_pct: number | null };
    p95_latency_ms: { current: number | null; previous: number | null; delta: number | null; delta_pct: number | null };
    db_changes_total: { current: number | null; previous: number | null; delta: number | null; delta_pct: number | null };
  } | null;
};

type AnomalyRow = {
  kind: "service" | "http_path";
  key: string;
  metric: "error_rate_pct" | "p95_latency_ms";
  current_value: number;
  previous_value: number;
  delta_value: number;
  delta_pct: number | null;
};

type AnomaliesResponse = {
  range: { from: string; to: string; tz: string };
  compare: { from: string; to: string };
  items: AnomalyRow[];
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function getTzDatePartsNow(tz: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);
  return { year, month, day };
}

function startOfDayTzIso(tzYear: number, tzMonth: number, tzDay: number) {
  return `${tzYear}-${pad2(tzMonth)}-${pad2(tzDay)}T00:00:00${TZ_OFFSET}`;
}

function endOfDayTzIso(tzYear: number, tzMonth: number, tzDay: number) {
  return `${tzYear}-${pad2(tzMonth)}-${pad2(tzDay)}T23:59:59${TZ_OFFSET}`;
}

function getPresetRange(preset: string): { fromIso: string; toIso: string; bucket: "day" | "hour" } {
  const { year, month, day } = getTzDatePartsNow(TZ);
  const todayStart = new Date(startOfDayTzIso(year, month, day));
  const todayEnd = new Date(endOfDayTzIso(year, month, day));

  if (preset === "today") {
    return { fromIso: todayStart.toISOString(), toIso: todayEnd.toISOString(), bucket: "hour" };
  }

  if (preset === "mtd") {
    const monthStart = new Date(`${year}-${pad2(month)}-01T00:00:00${TZ_OFFSET}`);
    return { fromIso: monthStart.toISOString(), toIso: todayEnd.toISOString(), bucket: "day" };
  }

  const days = preset === "30d" ? 30 : 7;
  const from = new Date(todayStart.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { fromIso: from.toISOString(), toIso: todayEnd.toISOString(), bucket: "day" };
}

function fmtCompact(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return x.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(n: number | null | undefined) {
  const x = typeof n === "number" && Number.isFinite(n) ? n : null;
  if (x === null) return "—";
  return `${x.toFixed(2)}%`;
}

function DeltaBadge({ deltaPct }: { deltaPct: number | null }) {
  if (deltaPct === null || !Number.isFinite(deltaPct)) {
    return <Badge variant="outline" className="font-normal">—</Badge>;
  }
  const up = deltaPct >= 0;
  return (
    <Badge variant={up ? "default" : "secondary"} className="font-normal">
      {up ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
      {Math.abs(deltaPct).toFixed(1)}%
    </Badge>
  );
}

const eventsChartConfig: ChartConfig = {
  // NOTE: our CSS chart tokens are `oklch(...)`, not HSL. Use them directly.
  total: { label: "Events", color: "var(--chart-1)" },
  errors: { label: "Errors", color: "var(--chart-2)" },
  p95: { label: "p95 latency (ms)", color: "var(--chart-3)" },
};

export function VIPAuditPage() {
  const [preset, setPreset] = useState<"today" | "7d" | "30d" | "mtd">("7d");
  const [compareOn, setCompareOn] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [anomalies, setAnomalies] = useState<AnomaliesResponse | null>(null);

  const range = useMemo(() => getPresetRange(preset), [preset]);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [s, a] = await Promise.all([
        auditApiV2Get<SummaryResponse>("summary", {
          from: range.fromIso,
          to: range.toIso,
          tz: TZ,
          bucket: range.bucket,
          top_limit: 10,
          compare: compareOn ? "previous_period" : null,
        }),
        compareOn
          ? auditApiV2Get<AnomaliesResponse>("insights/anomalies", {
              from: range.fromIso,
              to: range.toIso,
              tz: TZ,
              limit: 12,
              compare: "previous_period",
            })
          : Promise.resolve(null),
      ]);
      setSummary(s);
      setAnomalies(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load VIP Audit data.");
      setSummary(null);
      setAnomalies(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, compareOn]);

  const timeseries = summary?.current?.events?.timeseries ?? [];
  const chartData = timeseries.map((r) => ({
    t: r.t,
    total: Number(r.total ?? 0) || 0,
    errors: Number(r.errors ?? 0) || 0,
    p95: r.p95_latency_ms === null ? null : Number(r.p95_latency_ms),
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle>VIP Audit</CardTitle>
            <CardDescription>High-signal operational insights (with comparison).</CardDescription>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-normal text-muted-foreground">Timeframe</Label>
              <Select value={preset} onValueChange={(v) => setPreset(v as any)}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="mtd">Month to date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch id="vip-audit-compare" checked={compareOn} onCheckedChange={(v) => setCompareOn(Boolean(v))} />
              <Label htmlFor="vip-audit-compare" className="text-sm font-normal text-muted-foreground">
                Compare
              </Label>
            </div>

            <Button variant="outline" className="h-9 font-normal" onClick={loadAll} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>VIP Audit failed to load</AlertTitle>
          <AlertDescription className="break-words">{error}</AlertDescription>
        </Alert>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {loading || !summary ? (
          <>
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
            <Skeleton className="h-[96px]" />
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total events</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(summary.current.events.total)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">{compareOn && summary.delta ? <DeltaBadge deltaPct={summary.delta.events_total.delta_pct} /> : null}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Error rate</CardDescription>
                <CardTitle className="text-2xl">{fmtPct(summary.current.events.error_rate_pct)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">{compareOn && summary.delta ? <DeltaBadge deltaPct={summary.delta.error_rate_pct.delta_pct} /> : null}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>p95 latency (ms)</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(summary.current.events.latency_ms.p95)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">{compareOn && summary.delta ? <DeltaBadge deltaPct={summary.delta.p95_latency_ms.delta_pct} /> : null}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>DB changes</CardDescription>
                <CardTitle className="text-2xl">{fmtCompact(summary.current.db_changes.total)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">{compareOn && summary.delta ? <DeltaBadge deltaPct={summary.delta.db_changes_total.delta_pct} /> : null}</CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Events vs Errors</CardTitle>
            <CardDescription>Trend over time (bucket: {range.bucket}).</CardDescription>
          </CardHeader>
          <CardContent>
            {loading || !summary ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <ChartContainer config={eventsChartConfig} className="h-[260px] w-full">
                <AreaChart data={chartData} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="t"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                    tickFormatter={(v) => String(v).slice(5, 10)}
                  />
                  <YAxis tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                  <Area type="monotone" dataKey="total" stroke="var(--color-total)" fill="var(--color-total)" fillOpacity={0.15} />
                  <Area type="monotone" dataKey="errors" stroke="var(--color-errors)" fill="var(--color-errors)" fillOpacity={0.2} />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latency (p95)</CardTitle>
            <CardDescription>Operational latency over time.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading || !summary ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <ChartContainer config={eventsChartConfig} className="h-[260px] w-full">
                <LineChart data={chartData} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="t"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                    tickFormatter={(v) => String(v).slice(5, 10)}
                  />
                  <YAxis tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
                  <Line type="monotone" dataKey="p95" stroke="var(--color-p95)" dot={false} />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* What changed */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What changed</CardTitle>
          <CardDescription>Biggest spikes vs previous period (errors & latency).</CardDescription>
        </CardHeader>
        <CardContent>
          {!compareOn ? (
            <div className="text-sm text-muted-foreground">Turn on “Compare” to see anomalies.</div>
          ) : loading ? (
            <Skeleton className="h-[180px] w-full" />
          ) : !anomalies ? (
            <div className="text-sm text-muted-foreground">No anomaly data.</div>
          ) : anomalies.items.length === 0 ? (
            <div className="text-sm text-muted-foreground">No anomalies detected.</div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">Δ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {anomalies.items.slice(0, 12).map((r, idx) => (
                    <TableRow key={`${r.kind}-${r.metric}-${r.key}-${idx}`}>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="font-normal">
                          {r.kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[420px] truncate">{r.key}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="secondary" className="font-normal">
                          {r.metric}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm font-mono tabular-nums">
                        {r.delta_pct === null ? "—" : `${r.delta_pct >= 0 ? "+" : ""}${r.delta_pct.toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

